import { MailOutlined } from '@ant-design/icons';
import { Alert, Button, ConfigProvider, Input, Modal, Progress, Space, Table, Tag, Typography, message, theme } from 'antd';
import { useMemo, useRef, useState } from 'react';
import apis from '../../apis';
import { loadEventResultEmailRows } from '../../utils/loadEventResultEmailRows';
import { eligibleResultRows, requireSyncedResultRows, matchNonQualifierAttachments, readResultPdf, resultRecipientKey,
    singleCommentSheetProblem, singleNonQualifierProblem, validResultDates } from '../../utils/scoringResultEmails';

const { Text, Paragraph } = Typography;
const TEST_EMAIL = 'renaldolouis555@gmail.com';
const DEFAULT_WINNER_DATES = { confirmationDeadline: '6 October 2026', rundownReleaseDate: '19 October 2026' };
const blockedStatuses = ['sending', 'uncertain'];
const canSend = recipient => !['sent', 'alreadySent', ...blockedStatuses].includes(recipient.deliveryStatus);

const ScoringResultEmailSender = ({ eventId, category, rows, disabled, allWinners = false, getAgeCategoryLabel = value => value }) => {
    const [kind, setKind] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [files, setFiles] = useState([]);
    const [certificateFiles, setCertificateFiles] = useState([]);
    const [singleFiles, setSingleFiles] = useState({});
    const [winnerPdf, setWinnerPdf] = useState(null);
    const [dates, setDates] = useState(DEFAULT_WINNER_DATES);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [progress, setProgress] = useState({ completed: 0, total: 0 });
    const [summary, setSummary] = useState('');
    const campaignRows = useRef([]);
    const requestVersion = useRef(0);
    const folderInput = useRef(null);
    const certificateFolderInput = useRef(null);
    const singleInput = useRef(null);
    const singleCertificateInput = useRef(null);
    const singleTarget = useRef(null);
    const winnerInput = useRef(null);
    const busy = useRef(false);
    const [confirmationModal, confirmationContext] = Modal.useModal();

    const prepared = useMemo(() => kind === 'nonQualifier' ? matchNonQualifierAttachments(recipients, files, certificateFiles)
        : recipients.map(recipient => ({ ...recipient, attachment: winnerPdf,
            problem: recipient.problem || (!winnerPdf ? 'Choose the shared guidelines PDF' : '') })),
    [kind, recipients, files, certificateFiles, winnerPdf]);
    const pending = prepared.filter(canSend);
    const hasProblems = pending.some(recipient => recipient.problem);
    const ready = !loading && !sending && !testing && !error && pending.length > 0 && !hasProblems
        && (kind !== 'winner' || validResultDates(dates));

    async function openCampaign(nextKind) {
        const version = ++requestVersion.current;
        setKind(nextKind);
        setRecipients([]);
        setFiles([]);
        setCertificateFiles([]);
        setSingleFiles({});
        singleTarget.current = null;
        setWinnerPdf(null);
        setDates({ ...DEFAULT_WINNER_DATES });
        setError('');
        setSummary('');
        setProgress({ completed: 0, total: 0 });
        setLoading(true);
        try {
            if (eventId !== 'APCS2026') throw new Error('Result emails are available only for APCS2026.');
            const scopeRows = allWinners ? await loadEventResultEmailRows(eventId) : rows;
            if (version !== requestVersion.current) return;
            requireSyncedResultRows(scopeRows);
            campaignRows.current = scopeRows;
            const eligibleRows = eligibleResultRows(scopeRows, nextKind);
            const ids = eligibleRows.map(row => row.registrant.id);
            const ageCategoriesByRegistrant = new Map(eligibleRows.map(row => [row.registrant.id,
                getAgeCategoryLabel(row.registrant.ageCategory) || row.registrant.ageCategory || 'Unassigned']));
            const categoriesByRegistrant = new Map(eligibleRows.map(row => [row.registrant.id,
                row.registrant.competitionCategory || 'Unassigned']));
            const preview = [];
            for (let index = 0; index < ids.length; index += 40) {
                const result = await apis.email.previewScoringResults({ eventId, kind: nextKind,
                    registrantIds: ids.slice(index, index + 40) });
                preview.push(...result.recipients.map(recipient => ({ ...recipient,
                    competitionCategory: categoriesByRegistrant.get(recipient.registrantId),
                    ageCategory: ageCategoriesByRegistrant.get(recipient.registrantId) || 'Unassigned' })));
            }
            if (version === requestVersion.current) setRecipients(preview);
        } catch (err) {
            if (version === requestVersion.current) setError(err.message);
        } finally {
            if (version === requestVersion.current) setLoading(false);
        }
    }

    function closeCampaign() {
        if (sending || testing) return;
        requestVersion.current += 1;
        singleTarget.current = null;
        setKind(null);
    }

    async function sendTest() {
        if (busy.current) return;
        busy.current = true;
        setTesting(true);
        try {
            const attachment = kind === 'winner' && winnerPdf ? await readResultPdf(winnerPdf) : undefined;
            await apis.email.testScoringResults({ kind, dates, attachment });
            message.success(`SMTP accepted the test email to ${TEST_EMAIL}. Check your inbox.`);
        } catch (err) {
            message.error(err.message);
        } finally {
            setTesting(false);
            busy.current = false;
        }
    }

    async function sendCampaign() {
        if (!ready || busy.current) return;
        busy.current = true;
        setSending(true);
        try {
            const latestRows = allWinners ? await loadEventResultEmailRows(eventId) : rows;
            requireSyncedResultRows(latestRows);
            if (allWinners) {
                const winnerIds = source => eligibleResultRows(source, 'winner').map(row => row.registrant.id).sort();
                if (JSON.stringify(winnerIds(latestRows)) !== JSON.stringify(winnerIds(campaignRows.current))) {
                    throw new Error('The winner list changed. Close and reopen the preview before sending.');
                }
            }
        } catch (err) {
            setError(err.message);
            setSending(false);
            busy.current = false;
            return;
        }
        setSummary('');
        setProgress({ completed: 0, total: pending.length });
        const counts = { sent: 0, alreadySent: 0, failed: 0, uncertain: 0 };
        try {
            // Read and validate every chosen file before sending the first email.
            const chosenFiles = new Set(pending.flatMap(recipient => kind === 'nonQualifier'
                ? [recipient.attachment, recipient.certificateAttachment] : [recipient.attachment]));
            for (const file of chosenFiles) await readResultPdf(file);
            // Retain only the current recipient's encoded PDFs, even for large selected folders.
            const sharedAttachment = kind === 'winner' ? await readResultPdf(winnerPdf) : null;
            for (let index = 0; index < pending.length; index += 1) {
                const recipient = pending[index];
                const attachment = sharedAttachment || await readResultPdf(recipient.attachment);
                const certificateAttachment = kind === 'nonQualifier'
                    ? await readResultPdf(recipient.certificateAttachment) : undefined;
                let result;
                try {
                    result = await apis.email.sendScoringResults({ eventId, kind, dates,
                        registrantId: recipient.registrantId, performerIndex: recipient.performerIndex,
                        snapshot: recipient.snapshot, attachment, certificateAttachment });
                } catch (err) {
                    // A timeout may occur after SMTP acceptance. Reopen the preview before retrying.
                    result = { status: 'uncertain', message: err.message };
                }
                counts[result.status] = (counts[result.status] || 0) + 1;
                setRecipients(current => current.map(item => resultRecipientKey(item) === resultRecipientKey(recipient)
                    ? { ...item, deliveryStatus: result.status, deliveryMessage: result.message || '' } : item));
                setProgress({ completed: index + 1, total: pending.length });
                if (result.status === 'uncertain') {
                    setError('Sending paused. Reopen the preview and check any uncertain delivery with the SMTP provider before retrying.');
                    break;
                }
                // Stay below the existing 100 requests/minute API limit.
                if (index + 1 < pending.length) await new Promise(resolve => setTimeout(resolve, 1000));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSummary(`${counts.sent} accepted by SMTP; ${counts.alreadySent} already sent; ${counts.failed} failed; ${counts.uncertain} require review.`);
            setSending(false);
            busy.current = false;
        }
    }

    function chooseSinglePdf(recipient, type) {
        singleTarget.current = recipient;
        (type === 'certificate' ? singleCertificateInput : singleInput).current?.click();
    }

    function onSinglePdfChosen(event, type) {
        const file = event.target.files?.[0];
        event.target.value = '';
        const recipient = singleTarget.current;
        singleTarget.current = null;
        if (!file || !recipient) return;
        const problem = singleCommentSheetProblem(recipient, file);
        if (problem) { message.error(problem); return; }
        setSingleFiles(current => ({ ...current, [resultRecipientKey(recipient)]: {
            ...current[resultRecipientKey(recipient)], [type]: file,
        } }));
    }

    async function sendOne(recipient, selected) {
        if (busy.current || !canSend(recipient)) return;
        const matchingRows = rows.filter(row => row.registrant?.id === recipient.registrantId);
        try {
            if (!matchingRows.length) throw new Error('Performer is no longer in the filtered table. Reopen the preview.');
            requireSyncedResultRows(matchingRows);
            const current = recipients.find(item => resultRecipientKey(item) === resultRecipientKey(recipient));
            const problem = singleNonQualifierProblem(current, selected?.comment, selected?.certificate);
            if (problem) throw new Error(problem);
        } catch (err) {
            setError(err.message);
            return;
        }
        busy.current = true;
        setSending(true);
        setSummary('');
        try {
            const attachment = await readResultPdf(selected.comment);
            const certificateAttachment = await readResultPdf(selected.certificate);
            let result;
            try {
                result = await apis.email.sendScoringResults({ eventId, kind: 'nonQualifier',
                    registrantId: recipient.registrantId, performerIndex: recipient.performerIndex,
                    snapshot: recipient.snapshot, attachment, certificateAttachment, manualAttachmentSelection: true });
            } catch (err) {
                result = { status: 'uncertain', message: err.message };
            }
            setRecipients(current => current.map(item => resultRecipientKey(item) === resultRecipientKey(recipient)
                ? { ...item, deliveryStatus: result.status, deliveryMessage: result.message || '' } : item));
            if (result.status === 'uncertain') {
                setError('Delivery outcome is uncertain. Check the SMTP provider before any retry.');
            }
            setSummary(result.status === 'sent' ? `SMTP accepted the email to ${recipient.email}.`
                : result.status === 'alreadySent' ? 'This performer was already sent an email.'
                    : result.message || `Single email status: ${result.status}.`);
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
            busy.current = false;
        }
    }

    const columns = [
        { title: 'Performer', dataIndex: 'name' },
        ...(allWinners ? [{ title: 'Competition Category', dataIndex: 'competitionCategory' }] : []),
        ...(kind === 'nonQualifier' ? [{ title: 'Age Category', dataIndex: 'ageCategory' }] : []),
        { title: 'Email', dataIndex: 'email' },
        { title: 'Award', dataIndex: 'award' },
        { title: kind === 'winner' ? 'Guidelines PDF' : 'Comment sheet', render: (_, recipient) => {
            const chosen = singleFiles[resultRecipientKey(recipient)];
            return chosen?.comment ? `${chosen.comment.name} (single)` : recipient.attachment?.name || 'Missing';
        } },
        ...(kind === 'nonQualifier' ? [{ title: 'E-certificate', render: (_, recipient) => {
            const chosen = singleFiles[resultRecipientKey(recipient)];
            return chosen?.certificate ? `${chosen.certificate.name} (single)` : recipient.certificateAttachment?.name || 'Missing';
        } }] : []),
        { title: 'Status', render: (_, recipient) => {
            const problem = canSend(recipient) ? recipient.problem : '';
            const status = problem || recipient.deliveryMessage || recipient.deliveryStatus;
            return <Tag color={problem || blockedStatuses.includes(recipient.deliveryStatus) ? 'red'
                : ['sent', 'alreadySent'].includes(recipient.deliveryStatus) ? 'green' : 'gold'}>{status}</Tag>;
        } },
        ...(kind === 'nonQualifier' ? [{ title: 'Send one', width: 390, render: (_, recipient) => {
            const current = recipients.find(item => resultRecipientKey(item) === resultRecipientKey(recipient));
            const selected = singleFiles[resultRecipientKey(recipient)];
            const problem = singleNonQualifierProblem(current, selected?.comment, selected?.certificate);
            return <Space size="small">
                    <Button size="small" disabled={sending || testing || loading || !canSend(recipient) || Boolean(current?.problem)}
                        onClick={() => chooseSinglePdf(current, 'comment')}>Choose Comment</Button>
                    <Button size="small" disabled={sending || testing || loading || !canSend(recipient) || Boolean(current?.problem)}
                        onClick={() => chooseSinglePdf(current, 'certificate')}>Choose Certificate</Button>
                    <Button size="small" type="primary" disabled={sending || testing || loading || !canSend(recipient) || Boolean(problem)}
                        onClick={() => confirmationModal.confirm({
                            title: 'Send one non-qualifier email?',
                            content: <div>
                                <p>Performer: {recipient.name}</p>
                                <p>Age category: {recipient.ageCategory}</p>
                                <p>Email: {recipient.email}</p>
                                <p>Registration: {recipient.registrantId} · Performer #{recipient.performerIndex + 1}</p>
                                <p>Comment sheet: {selected?.comment?.name}</p>
                                <p>E-certificate: {selected?.certificate?.name}</p>
                                <p>This sends only to this selected performer. Check duplicate names and addresses before continuing.</p>
                            </div>,
                            okText: 'Send One Email', onOk: () => sendOne(recipient, selected),
                            styles: { content: { background: '#1E1E1E', color: '#e5cc92' }, header: { background: '#1E1E1E' } },
                        })}>Send One</Button>
            </Space>;
        } }] : []),
    ];

    return <ConfigProvider theme={{ algorithm: theme.darkAlgorithm, token: {
        colorPrimary: '#EBBC64', colorBgContainer: '#1E1E1E', colorBgElevated: '#1E1E1E', colorText: '#e5cc92',
    } }}>
        {confirmationContext}
        <Button icon={<MailOutlined />} disabled={disabled || eventId !== 'APCS2026'} onClick={() => openCampaign('winner')}>
            {allWinners ? 'Send to All Winners' : 'Send Winner Emails'}
        </Button>
        {!allWinners && <Button icon={<MailOutlined />} disabled={disabled || eventId !== 'APCS2026'} onClick={() => openCampaign('nonQualifier')}>
            Send Non-Qualifier Emails
        </Button>}
        <Modal open={Boolean(kind)} title={kind === 'winner' ? (allWinners ? 'APCS2026 — All Winner Invitations' : 'Winner Invitations') : 'Non-Qualifier Results'}
            width={1050} onCancel={closeCampaign} closable={!sending && !testing} maskClosable={false}
            keyboard={!sending && !testing} styles={{ content: { background: '#1E1E1E', color: '#e5cc92' },
                header: { background: '#1E1E1E' } }} footer={<Space wrap>
                <Button disabled={sending || testing} onClick={closeCampaign}>Close</Button>
                <Button loading={testing} disabled={sending || loading} onClick={sendTest}>Send Test Email</Button>
                <Button type="primary" loading={sending} disabled={!ready} onClick={() => confirmationModal.confirm({
                    title: `Send ${pending.length} performer emails?`,
                    content: kind === 'winner'
                        ? 'Each performer will receive the listed guidelines PDF at their own email address.'
                        : 'Each performer will receive the listed E-certificate and comment sheet PDFs at their own email address.',
                    okText: 'Send Emails', onOk: sendCampaign,
                    styles: { content: { background: '#1E1E1E', color: '#e5cc92' }, header: { background: '#1E1E1E' } },
                })}>Send {pending.length} Emails</Button>
            </Space>}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert type="info" showIcon message={allWinners
                    ? `${eventId} — all competition categories, finalized results only; table filters do not apply`
                    : `${eventId} / ${category || 'No category'} — current filtered table, finalized results only`}
                    description={`${recipients.length} performers in this preview. Sync Awards and refresh Scoring Recap after score or penalty edits. Winners receive guidelines only. Tests use Alex Example and go only to ${TEST_EMAIL}.`} />
                {error && <Alert type="error" showIcon message={error} />}
                {summary && <Alert type="info" message={summary} />}
                {kind === 'winner' ? <>
                    <Button disabled={sending || testing} onClick={() => winnerInput.current?.click()}>Choose Shared Guidelines PDF</Button>
                    <input ref={winnerInput} hidden type="file" accept=".pdf" disabled={sending || testing}
                        onChange={event => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (!file) return;
                            if (file.size > 4 * 1024 * 1024) { message.error('PDF must be at most 4 MB.'); return; }
                            setWinnerPdf(file);
                        }} />
                    {winnerPdf && <Text>{winnerPdf.name}</Text>}
                    <Input aria-label="Confirmation deadline" placeholder="Confirmation deadline, including time zone if applicable"
                        maxLength={160} disabled={sending || testing} value={dates.confirmationDeadline}
                        onChange={event => setDates(current => ({ ...current, confirmationDeadline: event.target.value }))} />
                    <Input aria-label="Rundown release date" placeholder="Rundown release date" maxLength={160}
                        disabled={sending || testing} value={dates.rundownReleaseDate}
                        onChange={event => setDates(current => ({ ...current, rundownReleaseDate: event.target.value }))} />
                    <Paragraph>Review these dates before sending. Sapphire results appear as Diamond in this invitation; the admin team will announce Sapphire separately.</Paragraph>
                </> : <>
                    <Button disabled={sending || testing} onClick={() => folderInput.current?.click()}>Choose Comment Sheet Folder</Button>
                    <input ref={folderInput} hidden type="file" webkitdirectory="" directory="" multiple
                        disabled={sending || testing} onChange={event => {
                            setFiles(Array.from(event.target.files || [])); event.target.value = '';
                        }} />
                    <Button disabled={sending || testing} onClick={() => certificateFolderInput.current?.click()}>Choose E-Certificate Folder</Button>
                    <input ref={certificateFolderInput} hidden type="file" webkitdirectory="" directory="" multiple
                        disabled={sending || testing} onChange={event => {
                            setCertificateFiles(Array.from(event.target.files || [])); event.target.value = '';
                        }} />
                    <input ref={singleInput} hidden type="file" accept=".pdf" disabled={sending || testing}
                        onChange={event => onSinglePdfChosen(event, 'comment')} />
                    <input ref={singleCertificateInput} hidden type="file" accept=".pdf" disabled={sending || testing}
                        onChange={event => onSinglePdfChosen(event, 'certificate')} />
                    <Paragraph>Select separate comment-sheet and E-certificate folders. Each folder needs one PDF named exactly after each performer, for example “Alex Example.pdf”. Case and surrounding spaces are ignored. Subfolders are included; duplicate names or PDFs block matching. Each PDF must be at most 4 MB.</Paragraph>
                    <Paragraph>To send only one performer, choose both PDFs on their row and use Send One. This works even when duplicate names prevent automatic folder matching.</Paragraph>
                    <Text>{files.filter(file => /\.pdf$/i.test(file.name)).length} comment sheets and {certificateFiles.filter(file => /\.pdf$/i.test(file.name)).length} E-certificates selected</Text>
                </>}
                {hasProblems && <Alert type="warning" message={kind === 'nonQualifier'
                    ? 'Batch sending requires unique performer names and both matching PDFs. You can choose both PDFs and send an eligible row individually.'
                    : 'Correct the missing performer details or attachment matches before sending.'} />}
                {prepared.some(recipient => blockedStatuses.includes(recipient.deliveryStatus))
                    && <Alert type="warning" message="Sending or uncertain deliveries are excluded. Check the SMTP provider before staff repair any uncertain delivery record." />}
                <Table size="small" loading={loading} dataSource={prepared} columns={columns}
                    rowKey={resultRecipientKey} pagination={{ pageSize: 10 }} scroll={{ x: kind === 'nonQualifier' ? 1450 : 900 }} />
                {progress.total > 0 && <Progress percent={Math.round(progress.completed / progress.total * 100)}
                    format={() => `${progress.completed}/${progress.total} processed`} />}
            </Space>
        </Modal>
    </ConfigProvider>;
};

export default ScoringResultEmailSender;
